import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Lock, LogOut, FileText, Image as ImageIcon, ArrowRightLeft, BookOpen, Briefcase, History, AlertCircle, Check, Loader2, ShieldCheck, MessageSquare, Send, Clock, XCircle, CheckCircle2, ExternalLink, BarChart3, LayoutList } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import CoverageReport from "@/components/seo/CoverageReport";
import SectionsEditor from "@/components/seo/SectionsEditor";
import { previewCompliance } from "@/utils/compliancePreview";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "seo_editor_token";

function authHeaders() {
  const token = localStorage.getItem(TOKEN_KEY);
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ── 401 interceptor: cleanly handle expired sessions ─────────────────────
// If any SEO-portal request returns 401 "Invalid token" or "Not authenticated",
// drop the stale token, alert the user, and reload to the login screen.
let _sessionExpiredHandled = false;
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || "";
    const detail = error?.response?.data?.detail || "";
    const isSeoCall = url.includes("/api/seo/") || url.includes("/seo/");
    const isAuthFailure =
      status === 401 &&
      (detail === "Invalid token" || detail === "Not authenticated" || detail === "Account inactive or not found");
    // Don't auto-redirect on the /seo/auth/login call itself (let it surface the credential error).
    const isLoginCall = url.includes("/seo/auth/login");
    if (isSeoCall && isAuthFailure && !isLoginCall && !_sessionExpiredHandled) {
      _sessionExpiredHandled = true;
      localStorage.removeItem(TOKEN_KEY);
      try { toast.error("Your session has expired. Please log in again.", { duration: 5000 }); } catch (_e) { /* toast unavailable, skip */ }
      // Give the toast a beat to render, then send them to the login screen.
      setTimeout(() => { window.location.href = "/seo-admin"; }, 800);
    }
    return Promise.reject(error);
  }
);

// ── LOGIN ─────────────────────────────────────────────────────────────────
function LoginScreen({ onLoginSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const res = await axios.post(`${API}/seo/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      onLoginSuccess(res.data.editor);
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0F172A] p-6">
      <Card className="w-full max-w-md bg-white">
        <CardHeader>
          <div className="flex items-center gap-2 mb-2">
            <Lock className="w-5 h-5 text-[#F97316]" />
            <span className="text-xs font-mono uppercase tracking-widest text-[#F97316]">SEO Editor Portal</span>
          </div>
          <CardTitle className="text-2xl text-[#0F172A]">Ezeefit SEO Admin</CardTitle>
          <p className="text-sm text-[#64748B] mt-2">Restricted access — agency editing portal. All changes are audit-logged and compliance-scrubbed.</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4" data-testid="seo-login-form" autoComplete="off">
            <div>
              <Label htmlFor="seo-email">Email</Label>
              <Input id="seo-email" name="seo-portal-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="seo-login-email" autoComplete="off" />
            </div>
            <div>
              <Label htmlFor="seo-pwd">Password</Label>
              <Input id="seo-pwd" name="seo-portal-pwd" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="seo-login-password" autoComplete="new-password" />
            </div>
            {error && (
              <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 p-3 rounded">
                <AlertCircle className="w-4 h-4 shrink-0" />
                <span>{error}</span>
              </div>
            )}
            <Button type="submit" disabled={loading} className="w-full bg-[#F97316] hover:bg-[#EA7515] text-white" data-testid="seo-login-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign In"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

// ── PAGES EDITOR ──────────────────────────────────────────────────────────
function PagesEditor() {
  const [pages, setPages] = useState([]);
  const [selectedPath, setSelectedPath] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/seo/pages`, { headers: authHeaders() }).then((r) => setPages(r.data || []));
  }, []);

  const loadPage = (path) => {
    setSelectedPath(path);
    const found = pages.find((p) => p.path === path);
    setForm(found?.override || { path });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { path: selectedPath, ...form };
      delete payload._id;
      const res = await axios.post(`${API}/seo/pages/update`, payload, { headers: authHeaders() });
      const cr = res.data.compliance_replacements || [];
      if (res.data.status === "pending_approval") {
        toast.warning(`Sent for owner approval (top-5 page). You'll see it live once approved.`, { duration: 5000 });
      } else {
        toast.success(`Saved! ${cr.length > 0 ? `${cr.length} term(s) auto-corrected for compliance.` : ""}`);
        if (cr.length > 0) {
          cr.forEach((r) => toast.info(`Auto-fix: "${r.original}" → "${r.replacement}"`, { duration: 8000 }));
        }
        // Re-fetch the saved override so the form reflects scrubbed text immediately.
        try {
          const pathParam = selectedPath.startsWith("/") ? selectedPath.slice(1) : selectedPath;
          const fresh = await axios.get(`${API}/seo/pages/${pathParam}`, { headers: authHeaders() });
          if (fresh.data?.override) {
            setForm({ path: selectedPath, ...fresh.data.override });
          }
        } catch (e) { /* non-fatal */ }
      }
      // refresh
      const r2 = await axios.get(`${API}/seo/pages`, { headers: authHeaders() });
      setPages(r2.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div className="bg-emerald-50 border-2 border-emerald-300 p-4 rounded" data-testid="pages-tab-callout">
        <div className="flex items-start gap-2">
          <CheckCircle2 className="w-5 h-5 text-emerald-700 shrink-0 mt-0.5" />
          <div className="text-sm text-emerald-900">
            <strong className="block mb-1">Meta tags on this tab. Body content + H1/H2/paragraphs on the &ldquo;Body Content&rdquo; tab.</strong>
            This <strong>Pages</strong> tab edits meta title, meta description, canonical, H1 override, <strong>robots</strong>, full <strong>Open Graph</strong> set (title/desc/image/type/site_name/url/locale), full <strong>Twitter card</strong> set (card/title/desc/image + label1&hellip;data2), <strong>geo-targeting</strong> meta (region/placename/position), and JSON-LD schema. For changes to actual page <strong>body copy</strong> (headlines, paragraphs, subheadings, list items, button labels, internal anchor text) &mdash; use the <strong>&ldquo;Body Content&rdquo;</strong> tab. Image alt text lives on the <strong>&ldquo;Image Alt&rdquo;</strong> tab.
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
      <div className="border border-[#E2E8F0] bg-white rounded p-2 max-h-[70vh] overflow-y-auto">
        <p className="text-xs font-semibold uppercase tracking-wider text-[#64748B] p-2">Pages ({pages.length})</p>
        {pages.map((p) => (
          <button
            key={p.path}
            onClick={() => loadPage(p.path)}
            className={`w-full text-left p-2 rounded text-sm transition-colors ${selectedPath === p.path ? "bg-[#F97316] text-white" : "hover:bg-[#F1F5F9]"}`}
            data-testid={`page-list-${p.path === "/" ? "root" : p.path.replace(/\//g, "-")}`}
          >
            <div className="flex items-center justify-between gap-1">
              <span className="truncate">{p.title}</span>
              {p.gated && <Badge variant="outline" className="text-[9px] shrink-0">GATED</Badge>}
            </div>
            <div className="text-[10px] opacity-70 font-mono truncate">{p.path}</div>
            {p.override && <Check className="w-3 h-3 inline mt-1 text-green-600" />}
          </button>
        ))}
      </div>
      <div>
        {!selectedPath ? (
          <div className="text-center text-[#64748B] py-20">Select a page from the left to edit its SEO.</div>
        ) : (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <CardTitle className="text-base">Editing: <span className="font-mono text-sm">{selectedPath}</span></CardTitle>
                <button
                  type="button"
                  onClick={() => {
                    const url = `${window.location.origin}${selectedPath}?_seo_preview=${Date.now()}`;
                    window.open(url, "_blank", "noopener,noreferrer");
                  }}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-[#F97316] hover:text-[#EA580C] hover:underline transition-colors"
                  data-testid="btn-live-preview"
                  title="Open this page in a new tab with a cache-busting query so you can verify your edit is live"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  Live Preview
                </button>
              </div>
              {pages.find((p) => p.path === selectedPath)?.gated && (
                <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 p-2 rounded mt-2">
                  <ShieldCheck className="w-4 h-4" />
                  Top-5 page — your changes require owner approval before going live.
                </div>
              )}
            </CardHeader>
            <CardContent className="space-y-4">
              <Field label="Meta Title" value={form.meta_title || ""} max={60} onChange={(v) => setForm({ ...form, meta_title: v })} testId="field-meta-title" />
              <Field label="Meta Description" value={form.meta_description || ""} max={170} textarea onChange={(v) => setForm({ ...form, meta_description: v })} testId="field-meta-description" />
              <Field label="Canonical URL" value={form.canonical || ""} onChange={(v) => setForm({ ...form, canonical: v })} testId="field-canonical" />
              <Field label="H1 Override" value={form.h1 || ""} onChange={(v) => setForm({ ...form, h1: v })} testId="field-h1" />
              <Field
                label='Robots (e.g. "index, follow, max-image-preview:large")'
                value={form.robots || ""}
                onChange={(v) => setForm({ ...form, robots: v })}
                testId="field-robots"
              />

              <details className="border border-[#E2E8F0] rounded p-3 bg-slate-50" data-testid="details-og">
                <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wider text-[#334155]">
                  Open Graph (Facebook / LinkedIn preview)
                </summary>
                <div className="space-y-3 mt-3">
                  <Field label="OG Title" value={form.og_title || ""} onChange={(v) => setForm({ ...form, og_title: v })} testId="field-og-title" />
                  <Field label="OG Description" value={form.og_description || ""} textarea onChange={(v) => setForm({ ...form, og_description: v })} testId="field-og-description" />
                  <Field label="OG Image URL" value={form.og_image || ""} onChange={(v) => setForm({ ...form, og_image: v })} testId="field-og-image" />
                  <Field label='OG Type (e.g. "website" or "article")' value={form.og_type || ""} onChange={(v) => setForm({ ...form, og_type: v })} testId="field-og-type" />
                  <Field label="OG Site Name" value={form.og_site_name || ""} onChange={(v) => setForm({ ...form, og_site_name: v })} testId="field-og-site-name" />
                  <Field label="OG URL" value={form.og_url || ""} onChange={(v) => setForm({ ...form, og_url: v })} testId="field-og-url" />
                  <Field label='OG Locale (e.g. "en_IN")' value={form.og_locale || ""} onChange={(v) => setForm({ ...form, og_locale: v })} testId="field-og-locale" />
                </div>
              </details>

              <details className="border border-[#E2E8F0] rounded p-3 bg-slate-50" data-testid="details-twitter">
                <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wider text-[#334155]">
                  Twitter / X card
                </summary>
                <div className="space-y-3 mt-3">
                  <Field label='Twitter Card (e.g. "summary" or "summary_large_image")' value={form.twitter_card || ""} onChange={(v) => setForm({ ...form, twitter_card: v })} testId="field-twitter-card" />
                  <Field label="Twitter Title" value={form.twitter_title || ""} onChange={(v) => setForm({ ...form, twitter_title: v })} testId="field-twitter-title" />
                  <Field label="Twitter Description" value={form.twitter_description || ""} textarea onChange={(v) => setForm({ ...form, twitter_description: v })} testId="field-twitter-description" />
                  <Field label="Twitter Image URL" value={form.twitter_image || ""} onChange={(v) => setForm({ ...form, twitter_image: v })} testId="field-twitter-image" />
                  <div className="grid grid-cols-2 gap-3">
                    <Field label='Label 1 (e.g. "Written by")' value={form.twitter_label1 || ""} onChange={(v) => setForm({ ...form, twitter_label1: v })} testId="field-twitter-label1" />
                    <Field label='Data 1 (e.g. "Ezeefit")' value={form.twitter_data1 || ""} onChange={(v) => setForm({ ...form, twitter_data1: v })} testId="field-twitter-data1" />
                    <Field label='Label 2 (e.g. "Time to read")' value={form.twitter_label2 || ""} onChange={(v) => setForm({ ...form, twitter_label2: v })} testId="field-twitter-label2" />
                    <Field label='Data 2 (e.g. "2 minutes")' value={form.twitter_data2 || ""} onChange={(v) => setForm({ ...form, twitter_data2: v })} testId="field-twitter-data2" />
                  </div>
                </div>
              </details>

              <details className="border border-[#E2E8F0] rounded p-3 bg-slate-50" data-testid="details-geo">
                <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wider text-[#334155]">
                  Geo-targeting meta
                </summary>
                <div className="space-y-3 mt-3">
                  <Field label='Geo Region (e.g. "IN" or "IN-MH")' value={form.geo_region || ""} onChange={(v) => setForm({ ...form, geo_region: v })} testId="field-geo-region" />
                  <Field label='Geo Placename (e.g. "Mumbai, Maharashtra")' value={form.geo_placename || ""} onChange={(v) => setForm({ ...form, geo_placename: v })} testId="field-geo-placename" />
                  <Field label='Geo Position (e.g. "19.1360;72.8344")' value={form.geo_position || ""} onChange={(v) => setForm({ ...form, geo_position: v })} testId="field-geo-position" />
                </div>
              </details>

              <Field label="Schema JSON-LD (raw JSON)" value={form.schema_jsonld || ""} textarea rows={8} mono onChange={(v) => setForm({ ...form, schema_jsonld: v })} testId="field-schema" />
              <label className="flex items-start gap-2 p-3 border border-amber-200 bg-amber-50 rounded text-xs text-amber-900 cursor-pointer" data-testid="field-disable-default-schema-wrap">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={!!form.disable_default_schema}
                  onChange={(e) => setForm({ ...form, disable_default_schema: e.target.checked })}
                  data-testid="field-disable-default-schema"
                />
                <span>
                  <strong className="block">Replace default JSON-LD with my schema above</strong>
                  When ticked, the page&rsquo;s built-in Product / FAQ / Breadcrumb / Speakable schemas are removed, leaving only your custom JSON-LD above. Use this when GSC flags a duplicate or invalid schema and you want full control. Leave unticked to keep the defaults and just ADD your custom JSON-LD on top.
                </span>
              </label>
              <Button onClick={save} disabled={saving} className="bg-[#F97316] hover:bg-[#EA7515] text-white w-full" data-testid="page-save-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, max, textarea, rows = 3, mono = false, testId }) {
  const raw = value || "";
  // Backend applies compliance auto-replace BEFORE enforcing the length limit.
  // Mirror that here so the counter reflects what backend will actually validate,
  // and surface the replacements so the editor knows why the count differs.
  const { scrubbed, replacements } = previewCompliance(raw);
  const effectiveLen = scrubbed.length;
  const rawLen = raw.length;
  const willChange = replacements.length > 0;
  const overLimit = max && effectiveLen > max;
  const Comp = textarea ? Textarea : Input;
  return (
    <div>
      <div className="flex justify-between">
        <Label className="text-xs uppercase tracking-wider text-[#64748B]">{label}</Label>
        {max && (
          <span
            className={`text-xs ${overLimit ? "text-red-600 font-semibold" : "text-[#94A3B8]"}`}
            title={willChange ? `Raw length: ${rawLen}. After auto-compliance: ${effectiveLen}.` : undefined}
          >
            {effectiveLen}/{max}
            {willChange ? " (post-compliance)" : ""}
          </span>
        )}
      </div>
      <Comp
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        className={`mt-1 ${mono ? "font-mono text-xs" : ""}`}
        data-testid={testId}
      />
      {willChange && (
        <div className="text-[11px] text-amber-700 mt-1 leading-snug" data-testid={`${testId}-compliance-notice`}>
          <strong>Auto-compliance will replace:</strong>{" "}
          {replacements.map((r, i) => (
            <span key={i}>
              <span className="line-through">{r.original}</span> → <em>&ldquo;{r.replacement}&rdquo;</em>
              {i < replacements.length - 1 ? "; " : ""}
            </span>
          ))}
          {max && overLimit && (
            <div className="text-red-600 mt-1">
              Result exceeds {max} chars &mdash; shorten the copy or avoid the flagged term.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── REDIRECTS EDITOR ─────────────────────────────────────────────────────
function RedirectsEditor() {
  const [items, setItems] = useState([]);
  const [from, setFrom] = useState(""); const [to, setTo] = useState(""); const [code, setCode] = useState(301);

  const refresh = () => axios.get(`${API}/seo/redirects`, { headers: authHeaders() }).then((r) => setItems(r.data || []));
  useEffect(() => { refresh(); }, []);

  const save = async () => {
    if (!from || !to) { toast.error("From and To paths required"); return; }
    try {
      await axios.post(`${API}/seo/redirects/update`, { from_path: from, to_path: to, status_code: Number(code), is_active: true }, { headers: authHeaders() });
      toast.success("Redirect saved");
      setFrom(""); setTo(""); setCode(301); refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const del = async (path) => {
    try {
      await axios.delete(`${API}/seo/redirects/${encodeURIComponent(path).replace(/%2F/g, "/")}`, { headers: authHeaders() });
      toast.success("Redirect deleted"); refresh();
    } catch (e) { toast.error("Delete failed"); }
  };

  return (
    <div className="space-y-4">
      <div className="bg-emerald-50 border-2 border-emerald-300 p-4 rounded text-sm text-emerald-900" data-testid="redirects-howto">
        <strong className="block mb-1">Redirects go live instantly &mdash; no approval needed.</strong>
        Path format: start with <code>/</code>, no domain prefix, no trailing slash. Examples:
        <ul className="list-disc pl-5 mt-1.5 space-y-0.5 text-xs">
          <li><strong>From:</strong> <code>/products/linear-workstations</code> &nbsp;<strong>To:</strong> <code>/products/linear-workstations-mumbai</code></li>
          <li><strong>From:</strong> <code>/old-blog-slug</code> &nbsp;<strong>To:</strong> <code>/blog/new-keyword-rich-slug</code></li>
          <li><strong>From:</strong> <code>/office-chairs-mumbai</code> &nbsp;<strong>To:</strong> <code>https://ezeefitofficesystems.in/products/executive-chairs</code> (external full URL also accepted)</li>
        </ul>
        <p className="mt-2 text-xs font-semibold" data-testid="redirects-rename-tip">Renaming a URL? Add only ONE rule: old URL → new URL. The new URL automatically shows the old page&rsquo;s content, sections and edits. Never add the reverse rule too &mdash; that creates a loop (the portal now blocks it).</p>
        <p className="mt-2 text-xs">Query strings (<code>?utm_source=...</code>) and hash fragments are auto-preserved through the redirect, so Google Ads / analytics tracking stays intact. Status 301 = permanent (preferred for SEO). 302 = temporary.</p>
      </div>
      <Card>
        <CardHeader><CardTitle className="text-base">Add / Update Redirect</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Input placeholder="From path (e.g. /old-page)" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="redirect-from" />
          <Input placeholder="To path or URL" value={to} onChange={(e) => setTo(e.target.value)} data-testid="redirect-to" />
          <Input placeholder="Status code" value={code} onChange={(e) => setCode(e.target.value)} type="number" data-testid="redirect-code" />
          <Button onClick={save} className="bg-[#F97316] hover:bg-[#EA7515] text-white" data-testid="redirect-save">Save</Button>
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-base">Active Redirects ({items.length})</CardTitle></CardHeader>
        <CardContent>
          {items.length === 0 ? <p className="text-sm text-[#94A3B8]">No redirects defined.</p> : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-[#E2E8F0] text-xs uppercase text-[#64748B]">
                  <th className="py-2">From</th><th>To</th><th>Code</th><th>Active</th><th></th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.from_path} className="border-b border-[#F1F5F9]">
                    <td className="py-2 font-mono text-xs">{r.from_path}</td>
                    <td className="font-mono text-xs">{r.to_path}</td>
                    <td>{r.status_code}</td>
                    <td>{r.is_active ? "✅" : "❌"}</td>
                    <td><Button variant="ghost" size="sm" onClick={() => del(r.from_path)} data-testid={`redirect-delete-${r.from_path.replace(/\//g, "-")}`}>Delete</Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ── ROBOTS.TXT EDITOR ────────────────────────────────────────────────────
function RobotsEditor() {
  const [content, setContent] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    axios.get(`${API}/seo/robots`, { headers: authHeaders() }).then((r) => setContent(r.data?.content || ""));
  }, []);
  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/seo/robots`, { content }, { headers: authHeaders() });
      toast.success("robots.txt updated");
    } catch (e) { toast.error("Save failed"); }
    finally { setSaving(false); }
  };
  return (
    <Card>
      <CardHeader><CardTitle className="text-base">robots.txt</CardTitle></CardHeader>
      <CardContent>
        <Textarea value={content} onChange={(e) => setContent(e.target.value)} rows={14} className="font-mono text-xs" data-testid="robots-content" />
        <Button onClick={save} disabled={saving} className="mt-3 bg-[#F97316] hover:bg-[#EA7515] text-white" data-testid="robots-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
        </Button>
      </CardContent>
    </Card>
  );
}

// ── IMAGE ALT + TITLE EDITOR ─────────────────────────────────────────────
function ImageAltsEditor() {
  const [items, setItems] = useState([]);
  const [src, setSrc] = useState("");
  const [alt, setAlt] = useState("");
  const [titleText, setTitleText] = useState("");
  const refresh = () => axios.get(`${API}/seo/image-alts`, { headers: authHeaders() }).then((r) => setItems(r.data || []));
  useEffect(() => { refresh(); }, []);
  const save = async () => {
    if (!src) { toast.error("Image src required"); return; }
    if (!alt && !titleText) { toast.error("Provide alt text and/or title text"); return; }
    try {
      const r = await axios.post(
        `${API}/seo/image-alts/update`,
        { image_src: src, alt_text: alt, title_text: titleText },
        { headers: authHeaders() },
      );
      const cr = r.data.compliance_replacements || [];
      toast.success(`Saved.${cr.length ? ` ${cr.length} auto-correction(s).` : ""}`);
      setSrc(""); setAlt(""); setTitleText(""); refresh();
    } catch (e) { toast.error("Save failed"); }
  };
  return (
    <div className="space-y-4">
      <div className="bg-emerald-50 border border-emerald-200 p-3 rounded text-xs text-emerald-900">
        <strong>Both <code>alt</code> and <code>title</code> attributes:</strong> alt text is read by screen-readers and image search; title text shows as the hover tooltip and is occasionally used by SEO crawlers. You can edit either or both per image. Both go live instantly &mdash; no approval needed for any image, on any page.
      </div>
      <Card>
        <CardHeader><CardTitle className="text-base">Add Image Alt + Title Override</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div>
            <label className="text-xs font-medium text-[#475569] mb-1 block">Image src URL (full URL or /path)</label>
            <Input placeholder="https://ezeefitofficesystems.in/static/.../hero.jpg  or  /static/.../hero.jpg" value={src} onChange={(e) => setSrc(e.target.value)} data-testid="alt-src" />
          </div>
          <div>
            <label className="text-xs font-medium text-[#475569] mb-1 block">Alt text <span className="text-[#94A3B8]">(descriptive, under 125 chars)</span></label>
            <Textarea placeholder="Modular linear workstation cluster with cable management — Ezeefit Mumbai" value={alt} onChange={(e) => setAlt(e.target.value)} data-testid="alt-text" rows={2} />
          </div>
          <div>
            <label className="text-xs font-medium text-[#475569] mb-1 block">Title text <span className="text-[#94A3B8]">(hover tooltip, optional, under 80 chars)</span></label>
            <Textarea placeholder="Ezeefit modular linear workstations — Mumbai office furniture manufacturer" value={titleText} onChange={(e) => setTitleText(e.target.value)} data-testid="image-title-text" rows={2} />
          </div>
          <Button onClick={save} className="bg-[#F97316] hover:bg-[#EA7515] text-white" data-testid="alt-save">Save</Button>
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-base">Existing overrides ({items.length})</CardTitle></CardHeader>
        <CardContent>
          {items.length === 0 ? <p className="text-sm text-[#94A3B8]">None yet.</p> : items.map((it) => (
            <div key={it.image_src} className="border-b border-[#F1F5F9] py-2 text-xs space-y-1">
              <div className="font-mono text-[#64748B] truncate" title={it.image_src}>{it.image_src}</div>
              <div><span className="text-[#94A3B8]">alt:</span> {it.alt_text || <em className="text-[#CBD5E1]">(empty)</em>}</div>
              {(it.title_text || "") && <div><span className="text-[#94A3B8]">title:</span> {it.title_text}</div>}
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

// ── BLOGS / CASE STUDIES EDITORS ─────────────────────────────────────────
function ContentEditor({ resource, label }) {
  const [items, setItems] = useState([]); const [selected, setSelected] = useState(null); const [form, setForm] = useState({}); const [saving, setSaving] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ title: "", slug: "", category: "Office Furniture", excerpt: "", meta_description: "", content: "", image: "" });
  const [creating, setCreating] = useState(false);
  const refreshList = useCallback(() => axios.get(`${API}/seo/${resource}`, { headers: authHeaders() }).then((r) => setItems(r.data || [])), [resource]);
  useEffect(() => { refreshList(); }, [refreshList]);
  const open = async (slug) => {
    const r = await axios.get(`${API}/seo/${resource}/${slug}`, { headers: authHeaders() });
    setSelected(slug); setForm(r.data);
  };
  const save = async () => {
    setSaving(true);
    try {
      const payload = resource === "blogs"
        ? { title: form.title, meta_description: form.meta_description, excerpt: form.excerpt, content: form.content, category: form.category }
        : { title: form.title, scope: form.scope, challenge: form.challenge, solution: form.solution, outcome: form.outcome, meta_description: form.meta_description };
      const res = await axios.post(`${API}/seo/${resource}/${selected}/update`, payload, { headers: authHeaders() });
      const cr = res.data.compliance_replacements || [];
      toast.success(`Saved.${cr.length ? ` ${cr.length} auto-correction(s).` : ""}`);
      if (cr.length > 0) cr.forEach((r) => toast.info(`Auto-fix: "${r.original}" → "${r.replacement}"`, { duration: 7000 }));
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  const createBlog = async () => {
    if (!createForm.title.trim()) { toast.error("Title is required"); return; }
    if (!createForm.content.trim()) { toast.error("Content is required"); return; }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/seo/blogs/create`, createForm, { headers: authHeaders() });
      const cr = res.data.compliance_replacements || [];
      toast.success(`Blog created and published: /${res.data.slug}${cr.length ? ` (${cr.length} auto-fix)` : ""}`);
      if (cr.length > 0) cr.forEach((r) => toast.info(`Auto-fix: "${r.original}" → "${r.replacement}"`, { duration: 7000 }));
      setShowCreate(false);
      setCreateForm({ title: "", slug: "", category: "Office Furniture", excerpt: "", meta_description: "", content: "", image: "" });
      refreshList();
      // Auto-open the new blog for further editing
      setTimeout(() => open(res.data.slug), 400);
    } catch (e) { toast.error(e.response?.data?.detail || "Create failed"); }
    finally { setCreating(false); }
  };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
      <div className="border border-[#E2E8F0] bg-white rounded p-2 max-h-[70vh] overflow-y-auto">
        <div className="flex items-center justify-between p-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-[#64748B]">{label} ({items.length})</p>
          {resource === "blogs" && (
            <Button size="sm" onClick={() => setShowCreate(true)} className="h-6 px-2 text-[10px] bg-[#F97316] hover:bg-[#EA7515] text-white" data-testid="blogs-create-btn">
              + New
            </Button>
          )}
        </div>
        {items.map((b) => (
          <button key={b.slug} onClick={() => open(b.slug)} className={`w-full text-left p-2 rounded text-sm ${selected === b.slug ? "bg-[#F97316] text-white" : "hover:bg-[#F1F5F9]"}`} data-testid={`${resource}-list-${b.slug}`}>
            <div className="truncate font-medium">{b.title}</div>
            <div className="text-[10px] opacity-70 font-mono truncate">{b.slug}</div>
          </button>
        ))}
      </div>
      <div>
        {showCreate && resource === "blogs" ? (
          <Card data-testid="blogs-create-card">
            <CardHeader className="flex flex-row items-start justify-between">
              <div>
                <CardTitle className="text-base">Create New Blog</CardTitle>
                <p className="text-xs text-[#64748B] mt-1">Auto-publishes on save. Compliance-scrubbed &amp; audit-logged.</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowCreate(false)} className="text-[#64748B]" data-testid="blogs-create-cancel">
                <XCircle className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <Field label="Title (required)" value={createForm.title} onChange={(v) => setCreateForm({ ...createForm, title: v })} testId="blog-create-title" />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Slug (optional — auto from title)" value={createForm.slug} onChange={(v) => setCreateForm({ ...createForm, slug: v })} testId="blog-create-slug" />
                <Field label="Category" value={createForm.category} onChange={(v) => setCreateForm({ ...createForm, category: v })} testId="blog-create-category" />
              </div>
              <Field label="Meta Description" value={createForm.meta_description} max={170} textarea onChange={(v) => setCreateForm({ ...createForm, meta_description: v })} testId="blog-create-meta" />
              <Field label="Excerpt" value={createForm.excerpt} textarea rows={2} onChange={(v) => setCreateForm({ ...createForm, excerpt: v })} testId="blog-create-excerpt" />
              <Field label="Hero Image URL (optional)" value={createForm.image} onChange={(v) => setCreateForm({ ...createForm, image: v })} testId="blog-create-image" />
              <Field label="Content (HTML allowed, required)" value={createForm.content} textarea rows={14} mono onChange={(v) => setCreateForm({ ...createForm, content: v })} testId="blog-create-content" />
              <Button onClick={createBlog} disabled={creating} className="w-full bg-[#F97316] hover:bg-[#EA7515] text-white" data-testid="blog-create-submit">
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create & Publish"}
              </Button>
            </CardContent>
          </Card>
        ) : !selected ? <div className="text-center text-[#64748B] py-20">Select an item to edit{resource === "blogs" ? ' — or click "+ New" to create a blog' : ''}.</div> : (
          <Card><CardContent className="pt-6 space-y-4">
            <Field label="Title" value={form.title || ""} onChange={(v) => setForm({ ...form, title: v })} testId={`${resource}-title`} />
            <Field label="Meta Description" value={form.meta_description || ""} max={170} textarea onChange={(v) => setForm({ ...form, meta_description: v })} testId={`${resource}-meta`} />
            {resource === "blogs" ? (
              <>
                <Field label="Category" value={form.category || ""} onChange={(v) => setForm({ ...form, category: v })} testId="blog-category" />
                <Field label="Excerpt" value={form.excerpt || ""} textarea onChange={(v) => setForm({ ...form, excerpt: v })} testId="blog-excerpt" />
                <Field label="Content (HTML)" value={form.content || ""} textarea rows={14} mono onChange={(v) => setForm({ ...form, content: v })} testId="blog-content" />
              </>
            ) : (
              <>
                <Field label="Scope" value={form.scope || ""} textarea onChange={(v) => setForm({ ...form, scope: v })} testId="cs-scope" />
                <Field label="Challenge" value={form.challenge || ""} textarea onChange={(v) => setForm({ ...form, challenge: v })} testId="cs-challenge" />
                <Field label="Solution" value={form.solution || ""} textarea onChange={(v) => setForm({ ...form, solution: v })} testId="cs-solution" />
                <Field label="Outcome" value={form.outcome || ""} textarea onChange={(v) => setForm({ ...form, outcome: v })} testId="cs-outcome" />
              </>
            )}
            <Button onClick={save} disabled={saving} className="w-full bg-[#F97316] hover:bg-[#EA7515] text-white" data-testid={`${resource}-save`}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
            </Button>
          </CardContent></Card>
        )}
      </div>
    </div>
  );
}

// ── AUDIT LOG ────────────────────────────────────────────────────────────
function AuditLog() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    axios.get(`${API}/seo/audit-log?limit=200`, { headers: authHeaders() }).then((r) => setItems(r.data || []));
  }, []);
  return (
    <Card><CardHeader><CardTitle className="text-base">Your Recent Edits ({items.length})</CardTitle></CardHeader><CardContent>
      {items.length === 0 ? <p className="text-sm text-[#94A3B8]">No edits yet.</p> : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b border-[#E2E8F0] text-xs uppercase text-[#64748B]">
              <th className="py-2">When</th><th>Resource</th><th>ID</th><th>Action</th><th>Auto-fixes</th><th>Gated</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} className="border-b border-[#F1F5F9]">
                <td className="py-2 text-xs">{new Date(it.timestamp).toLocaleString()}</td>
                <td>{it.resource_type}</td>
                <td className="font-mono text-xs truncate max-w-[260px]">{it.resource_id}</td>
                <td>{it.action}</td>
                <td>{it.compliance_replacements?.length || 0}</td>
                <td>{it.gated ? "🔒" : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </CardContent></Card>
  );
}

// ── CONTENT CHANGE REQUESTS ───────────────────────────────────────────────
function ContentRequestsEditor() {
  const [requests, setRequests] = useState([]);
  const [form, setForm] = useState({ page_path: "/", section: "", current_text: "", proposed_text: "", reason: "" });
  const [submitting, setSubmitting] = useState(false);

  const refresh = async () => {
    try {
      const r = await axios.get(`${API}/seo/content-requests`, { headers: authHeaders() });
      setRequests(r.data || []);
    } catch (e) {
      toast.error("Failed to load your requests");
    }
  };
  useEffect(() => { refresh(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.page_path || !form.current_text || !form.proposed_text || !form.reason) {
      toast.error("Page path, Current copy, Proposed copy and SEO reasoning are required");
      return;
    }
    // Section is just a label for the audit log — auto-fill from current_text if blank
    // so Speicher never gets blocked by "which section is this?" confusion.
    const payload = { ...form, section: form.section?.trim() || form.current_text.slice(0, 60).trim() };
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/seo/content-requests`, payload, { headers: authHeaders() });
      const cr = r.data.compliance_replacements || [];
      if (r.data.gated) {
        toast.warning("Submitted for owner approval (top-5 page). You'll see it live once approved.", { duration: 6000 });
      } else {
        toast.success("Live on the page now. Owner will see it in their audit log.");
      }
      if (cr.length > 0) {
        cr.forEach((rep) => toast.info(`Auto-fix: "${rep.original}" → "${rep.replacement}"`, { duration: 8000 }));
      }
      setForm({ page_path: "/", section: "", current_text: "", proposed_text: "", reason: "" });
      refresh();
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  const statusBadge = (s) => {
    if (s === "approved") return <Badge className="bg-green-100 text-green-800 hover:bg-green-100"><CheckCircle2 className="w-3 h-3 mr-1" /> Approved</Badge>;
    if (s === "rejected") return <Badge variant="outline" className="text-red-700 border-red-300"><XCircle className="w-3 h-3 mr-1" /> Rejected</Badge>;
    return <Badge variant="outline" className="text-amber-700 border-amber-300"><Clock className="w-3 h-3 mr-1" /> Pending</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="content-requests-editor">
      <div className="bg-green-50 border-2 border-green-300 p-4 rounded">
        <div className="flex items-start gap-2">
          <CheckCircle2 className="w-5 h-5 text-green-700 shrink-0 mt-0.5" />
          <div className="text-sm text-green-900">
            <strong className="block mb-1">This is the Body Content editor &mdash; works for FAQ items, individual cards, list bullets, anything visible on the page.</strong>
            Paste any text from a live page in the &ldquo;Current copy&rdquo; field, write your replacement in &ldquo;Proposed copy&rdquo;, and submit. The system finds that text in the live page DOM and swaps it instantly (for non-gated pages) or after owner approval (for the 5 gated pages). Works for H1, H2, paragraphs, list items, button labels &mdash; anything that renders as text on the live page.
          </div>
        </div>
      </div>
      <div className="bg-slate-50 border border-slate-300 p-4 rounded text-xs text-slate-800" data-testid="section-edit-howto">
        <strong className="block mb-2 text-sm text-slate-900">How to edit specific sections (FAQ items, cards, list bullets, to-do items)</strong>
        <p className="mb-2">The editor is a precise text-replacement engine. To target a specific section you paste just the text you want to change &mdash; not the whole page. Examples:</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li><strong>Single FAQ question</strong> &mdash; Paste only the question (e.g. <em>&ldquo;What is the standard dimension of a linear workstation seat?&rdquo;</em>) into <strong>Current copy</strong>, then your new question into <strong>Proposed copy</strong>. To edit the answer, repeat the same flow with the answer paragraph.</li>
          <li><strong>One specific card</strong> in a card grid &mdash; Paste only that card&rsquo;s headline or body text (e.g. <em>&ldquo;Modular Cabin Partitions&rdquo;</em>). Other cards stay untouched.</li>
          <li><strong>One bullet in a to-do / feature list</strong> &mdash; Paste only that bullet&rsquo;s text. Sibling bullets are not affected.</li>
          <li><strong>Button labels, CTAs, anchor text</strong> &mdash; Paste the visible label (e.g. <em>&ldquo;Request Consultation&rdquo;</em>).</li>
          <li><strong>Order-sensitive duplicates</strong> &mdash; If the same exact text appears 3 times on a page, add 2-3 words of context before/after so the replacement is unambiguous. Example: instead of <em>&ldquo;Learn more&rdquo;</em> alone, paste <em>&ldquo;Linear Workstations Learn more&rdquo;</em>.</li>
          <li><strong>Tip</strong> &mdash; use the <code>?seo-audit=1</code> URL bypass (e.g. <code>ezeefitofficesystems.in/products/linear-workstations?seo-audit=1</code>) to right-click and copy the exact text on the live page before pasting it into <strong>Current copy</strong>. This avoids invisible whitespace mismatches.</li>
        </ul>
        <p className="mt-2 text-slate-600">Once submitted, the replacement is applied via DOM hook the moment any visitor loads the page &mdash; no redeploy needed.</p>
      </div>
      <div className="bg-blue-50 border border-blue-200 p-3 rounded text-xs text-blue-900">
        <strong>Gating rules:</strong> Edits to <strong>non-gated pages</strong> (all 90+ pages except the 5 below) go live instantly. Edits to the <strong>5 gated pages</strong> (<code>/</code>, <code>/about</code>, <code>/products</code>, <code>/contact</code>, <code>/modular-office-workstations-mumbai</code>) need owner approval first. Every change &mdash; gated or not &mdash; is audit-logged with a full before/after diff.
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Send className="w-4 h-4 text-[#F97316]" /> Submit a New Copy Change Request</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-3" data-testid="content-request-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Page path</Label>
                <Input
                  value={form.page_path}
                  onChange={(e) => setForm({ ...form, page_path: e.target.value })}
                  placeholder="e.g. /about or /products"
                  data-testid="cr-page-path"
                />
              </div>
              <div>
                <Label className="text-xs">Section / location on page <span className="text-slate-400">(optional label)</span></Label>
                <Input
                  value={form.section}
                  onChange={(e) => setForm({ ...form, section: e.target.value })}
                  placeholder="Optional — leave blank if unsure. Used only for audit log."
                  data-testid="cr-section"
                />
                <p className="text-[10px] text-slate-500 mt-1">
                  This field is a label for your own record — it does <strong>not</strong> drive the match. The engine finds text by <strong>Current copy</strong> only.
                </p>
              </div>
            </div>
            <div>
              <Label className="text-xs">Current copy (paste from the live page)</Label>
              <Textarea
                rows={3}
                value={form.current_text}
                onChange={(e) => setForm({ ...form, current_text: e.target.value })}
                placeholder="Paste the current text exactly as it appears on the page"
                data-testid="cr-current-text"
              />
            </div>
            <div>
              <Label className="text-xs">Proposed copy</Label>
              <Textarea
                rows={3}
                value={form.proposed_text}
                onChange={(e) => setForm({ ...form, proposed_text: e.target.value })}
                placeholder="Your suggested new copy. Banned terms will be auto-corrected on save."
                data-testid="cr-proposed-text"
              />
            </div>
            <div>
              <Label className="text-xs">SEO reasoning (why this change improves rankings/UX)</Label>
              <Textarea
                rows={2}
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                placeholder="e.g. Adding the 'ergonomic workstations Mumbai' keyword to capture local search; current copy is too generic"
                data-testid="cr-reason"
              />
            </div>
            <Button type="submit" disabled={submitting} className="bg-[#F97316] hover:bg-[#EA7515] text-white" data-testid="cr-submit-btn">
              {submitting ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Submitting…</> : <><Send className="w-4 h-4 mr-1" /> Submit for Approval</>}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Your Submissions ({requests.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {requests.length === 0 ? (
            <p className="text-sm text-[#94A3B8] py-4 text-center">No submissions yet.</p>
          ) : (
            <div className="space-y-3">
              {requests.map((r) => (
                <div key={r.id} className="border border-[#E2E8F0] rounded p-3" data-testid={`cr-row-${r.id}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="text-xs text-[#64748B]">{new Date(r.submitted_at).toLocaleString()}</div>
                      <div className="font-mono text-sm text-[#0F172A]">{r.page_path} <span className="text-[#94A3B8]">·</span> {r.section}</div>
                    </div>
                    {statusBadge(r.status)}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                    <div className="bg-amber-50 p-2 rounded border border-amber-100">
                      <div className="font-semibold text-amber-800 mb-1">Current</div>
                      <div className="whitespace-pre-wrap">{r.current_text}</div>
                    </div>
                    <div className="bg-green-50 p-2 rounded border border-green-100">
                      <div className="font-semibold text-green-800 mb-1">Proposed</div>
                      <div className="whitespace-pre-wrap">{r.proposed_text}</div>
                    </div>
                  </div>
                  <div className="text-xs mt-2 text-[#64748B]"><strong>Reasoning:</strong> {r.reason}</div>
                  {r.decision_notes && (
                    <div className="text-xs mt-2 p-2 bg-[#F1F5F9] rounded">
                      <strong>Owner notes:</strong> {r.decision_notes}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}


// ── MAIN PORTAL ──────────────────────────────────────────────────────────
export default function SEOEditorPortal() {
  const [editor, setEditor] = useState(null);
  const [tab, setTab] = useState("pages");
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return;
    axios.get(`${API}/seo/auth/me`, { headers: authHeaders() })
      .then((r) => setEditor(r.data))
      .catch(() => localStorage.removeItem(TOKEN_KEY));
  }, []);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setEditor(null);
    // Hard reload so any cached form/state, autofill, and password manager hints are dropped.
    window.location.href = "/seo-admin";
  };

  if (!editor) return <LoginScreen onLoginSuccess={setEditor} />;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="bg-[#0F172A] text-white py-4 px-6 flex items-center justify-between">
        <div>
          <p className="text-[10px] font-mono uppercase tracking-widest text-[#F97316]">SEO Editor Portal</p>
          <h1 className="text-lg font-bold">Ezeefit Office Systems</h1>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="text-sm font-semibold">{editor.name}</div>
            <div className="text-xs text-[#94A3B8]">{editor.email} · {editor.edits_count} edits</div>
          </div>
          <Button variant="outline" size="sm" onClick={logout} className="text-white border-white/20 bg-transparent hover:bg-white/10" data-testid="seo-logout">
            <LogOut className="w-3.5 h-3.5 mr-1" /> Logout
          </Button>
        </div>
      </header>
      <div className="max-w-7xl mx-auto p-6">
        <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 p-3 rounded mb-4">
          <ShieldCheck className="w-4 h-4 shrink-0" />
          <span>All saves run an automated compliance scrub. Banned terms (superlatives, health language, named competitors) are auto-corrected. Edits to the top 5 pages need owner approval first.</span>
        </div>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-9 h-auto mb-4">
            <TabsTrigger value="pages" data-testid="tab-pages"><FileText className="w-3.5 h-3.5 mr-1" /> Pages</TabsTrigger>
            <TabsTrigger value="coverage" data-testid="tab-coverage"><BarChart3 className="w-3.5 h-3.5 mr-1" /> Coverage</TabsTrigger>
            <TabsTrigger value="blogs" data-testid="tab-blogs"><BookOpen className="w-3.5 h-3.5 mr-1" /> Blogs</TabsTrigger>
            <TabsTrigger value="case-studies" data-testid="tab-case-studies"><Briefcase className="w-3.5 h-3.5 mr-1" /> Case Studies</TabsTrigger>
            <TabsTrigger value="images" data-testid="tab-image-alt"><ImageIcon className="w-3.5 h-3.5 mr-1" /> Image Alt</TabsTrigger>
            <TabsTrigger value="content-requests" data-testid="tab-content-requests"><MessageSquare className="w-3.5 h-3.5 mr-1" /> Body Content</TabsTrigger>
            <TabsTrigger value="sections" data-testid="tab-sections"><LayoutList className="w-3.5 h-3.5 mr-1" /> New Sections</TabsTrigger>
            <TabsTrigger value="redirects" data-testid="tab-redirects"><ArrowRightLeft className="w-3.5 h-3.5 mr-1" /> Redirects</TabsTrigger>
            <TabsTrigger value="more" data-testid="tab-more">More</TabsTrigger>
          </TabsList>
          <TabsContent value="pages"><PagesEditor /></TabsContent>
          <TabsContent value="coverage"><CoverageReport authHeaders={authHeaders} /></TabsContent>
          <TabsContent value="blogs"><ContentEditor resource="blogs" label="Blogs" /></TabsContent>
          <TabsContent value="case-studies"><ContentEditor resource="case-studies" label="Case Studies" /></TabsContent>
          <TabsContent value="images"><ImageAltsEditor /></TabsContent>
          <TabsContent value="content-requests"><ContentRequestsEditor /></TabsContent>
          <TabsContent value="sections"><SectionsEditor authHeaders={authHeaders} /></TabsContent>
          <TabsContent value="redirects"><RedirectsEditor /></TabsContent>
          <TabsContent value="more">
            <Tabs defaultValue="robots">
              <TabsList>
                <TabsTrigger value="robots">robots.txt</TabsTrigger>
                <TabsTrigger value="audit"><History className="w-3.5 h-3.5 mr-1" /> Audit Log</TabsTrigger>
              </TabsList>
              <TabsContent value="robots"><RobotsEditor /></TabsContent>
              <TabsContent value="audit"><AuditLog /></TabsContent>
            </Tabs>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
